import { copyToClipboard } from "../../services/Utils/ClipboardUtil";
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Languages, MessagesSquare, Search, Info, XCircle, CheckCircle, Send, FileText, ChevronRight, Mic, Check, X, Share2, RefreshCw, Loader2 } from 'lucide-react';

import { useEffect, useReducer, useRef, useState } from "react";
import { Soribot } from "../../models/Soribot";
import { UserBlock } from "../../models/UserBlock";
import { FEATURE_USER_BLOCKING } from "../../config/features";
import { Fire, getFunc } from "../../services/FireClient";
import { My } from "../../services/My";
import { needsTranslation, toTransLang } from "../../services/translation/needsTranslation";
import { MessageLoader } from "../../services/MessageLoader";
import { ensureScrollWatch, ScrollEmitter, getScrolling } from "../../services/translation/scrollActivity";
import { getAutoTranslate, AutoTranslateEmitter } from "../../services/translation/autoTranslate";
// functions translate-api 의 PROMPT_VERSION 과 동기화 — 프롬프트 변경 시 함께 올려 기존 캐시 무효화
const TRANSLATE_PROMPT_VERSION = 4;
import { LongPress } from "../../services/Utils/LongPress";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { getDisplayName, resolveMessageUser } from "../../services/Utils/userDisplayName";
import { SendFile, Message } from '../../models/Message';
import { RoomToken } from '../../models/RoomToken';
import { askAI } from '../../services/AiMemoAnalyzer';
import { UIMessage } from "../components/UIMessage";
import { RoomRefreshEmitter } from "../rooms/RoomEvents";
import BotSourceTextModal from "../rooms/right-menu/BotSourceTextModal";
import { MemoModalEmitter } from "../rooms/right-menu/PanelMemos";
import { TaskEmitter } from "../tasks/TaskEmitter";
import { PostEmitter } from "../board/PostEmitter";
import BotMessageInfoModal from "../soribots/BotMessageInfoModal";
import MessageEmailItem from "./MessageEmailItem";
import MessageFileItem from "./MessageFileItem";
import MessagePopover from "./MessagePopover";
import { PlayMessageButton } from "./PlayMessageButton";
import { FileIcon } from "./MessageFileItem";
import { useAppUIContext } from '../../context/AppUIContext';
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage, AvatarStroke } from "../../components/ui/avatar";
import { UserAvatar } from "./UserAvatar";
import { Progress } from "../../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { IconSparkles, IconOutArrowRight } from './FigmaIcons';

export type MessageItemState = 'new' | 'playing' | 'paused' | 'old' | 'favorite';

interface IMessageItem {
  m: UIMessage,
  state: MessageItemState,
  onMessageClick: (msg: UIMessage) => void,
  parent?: UIMessage,
  isViewButton?: boolean,
  progressForFile?: { [key: string]: SendFile }
  onResendMessage?: (msg: UIMessage) => void,
  onReplyToClick?: (targetId: string) => void,
  highlightedReaction?: { messageId: string; emoticon: string } | null,
  reactionSlot?: React.ReactNode, // 🔧 아바타 아래 리액션 버튼 슬롯
  roomUsers?: { id: string; name?: string; photoUrl?: string | null }[],
  roomTokens?: RoomToken[],
}

const MessageItem: React.FC<IMessageItem> = ({ m, state, onMessageClick, parent, progressForFile, onResendMessage, onReplyToClick, isViewButton = false, highlightedReaction, reactionSlot, roomUsers, roomTokens }) => {
  // Room.users에서 최신 유저정보로 오버라이드 (방에 없는 유저는 메시지 저장 시점 정보 사용)
  const user = resolveMessageUser(m.msg.user, roomUsers);
  // 차단 기능 비표시 중에는 기존 차단 데이터가 있어도 메시지를 정상 표시
  const isBlocked = FEATURE_USER_BLOCKING ? My.UserBlocks.get(user.id) : undefined;
  const isDeleted = My.DeletedMessages.get(m.msg.id);
  const isNew = m.isNew();

  const [messageText, setMessageText] = useState<string | null>(null);
  const [showUnblockAlert, setShowUnblockAlert] = useState(false);
  const [showTranslateError, setShowTranslateError] = useState(false);
  const [showSummarizeError, setShowSummarizeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { toast } = useToast();
  const [longPress] = useState(new LongPress(() => copyTextToClipboard()));
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [expandable, setExpandable] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // 한 번 펼친 메시지(새 메시지 자동 펼침 또는 사용자가 직접 펼침)는 이후 읽음 처리돼도 다시 접지 않음
  const stayExpandedRef = useRef(false);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  // 자동번역 viewport 게이트: 화면에 보이는지(isIntersecting) + 스크롤 멈춤 여부(scrolling)
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [summarizedText, setSummarizedText] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [autoOn, setAutoOn] = useState(() => getAutoTranslate(m.msg.roomId));
  const { setMessageToShow } = useAppUIContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');


  useEffect(() => {
    setMessageText(getMessageText(m.msg.text));
    if (!m.isNew()) {
      setTimeout(() => {
        forceUpdate(); // Update again because the related notifiation object comes shortly afterwards, which will update isNew.
      }, 700);
    }
  }, [m.msg.text, m.msg.isEdited])

  // 방을 연 뒤 실시간으로 도착한 메시지인지 (isNew=미확인 알림은 보고 있는 방에선 안 켜져 못 씀 → 도착 경계로 판단)
  const isFreshArrival = MessageLoader.isFreshArrival(m.msg.roomId, m.msg.created);

  // 긴 메시지 접기 판단. 단 방을 연 뒤 도착한(isFreshArrival) 메시지는 접지 않고 바로 펼쳐서 보여줌
  // — 새 메시지마다 클릭해 펼쳐야 하는 불편 제거. 지난 메시지(스크롤백)만 접어 스크롤 부담을 던다.
  useEffect(() => {
    const divHeight = divRef.current?.offsetHeight ?? 0;
    const isLong = !!messageText && !messageText.includes('src=') && divHeight > 260;
    // 이미 펼치기로 확정된 메시지는 유지 (재렌더·읽음 전환으로 다시 접히지 않게)
    if (stayExpandedRef.current) { setExpandable(false); return; }
    if (!isLong) { setExpandable(false); return; }
    if (isFreshArrival) { stayExpandedRef.current = true; setExpandable(false); return; }
    setExpandable(true);
  }, [messageText, isFreshArrival])

  // 번역 pending 중(다른 사용자/세션이 번역 진행) 공유 캐시가 done 으로 채워지면 표시
  useEffect(() => {
    if (!isTranslating) return;
    const targetLang = i18n.locale === 'ko' ? 'ko' : i18n.locale === 'ja' ? 'ja' : 'en';
    const c = m.msg.translations?.[targetLang];
    if (c?.status === 'done' && c.text && c.pv === TRANSLATE_PROMPT_VERSION) {
      setTranslatedText(c.text);
      setIsTranslating(false);
    }
  }, [m.msg.translations, isTranslating])

  // 채널 자동 번역 토글 변경 구독
  useEffect(() => {
    const h = (rid: string) => { if (rid === m.msg.roomId) setAutoOn(getAutoTranslate(rid)); };
    AutoTranslateEmitter.on('change', h);
    return () => { AutoTranslateEmitter.removeListener('change', h); };
  }, [m.msg.roomId])

  // 자동번역 viewport 게이트(1): 번역 대상 메시지가 화면에 보이는지(isIntersecting) 추적.
  useEffect(() => {
    if (!autoOn || translatedText || !m.msg.text) return;
    // 발신자 본인 메시지는 자동번역 제외 — 내가 쓴 문장은 원 의도를 이미 알고,
    // 외국어로 보낸 경우 되돌리면 왕복 번역(KO→JA→KO) 열화 + 나 혼자만 소비해 공유 캐시 amortization 불가 → 순수 낭비.
    if (user.id === My.user.id) return;
    if (!needsTranslation(m.msg.text, toTransLang(i18n.locale))) return;
    const el = rootRef.current;
    if (!el) return;
    ensureScrollWatch();
    const obs = new IntersectionObserver(([entry]) => setIsIntersecting(entry.isIntersecting), { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOn, translatedText, m.msg.id, m.msg.text])

  // 자동번역 viewport 게이트(2): 스크롤 멈춤 상태 구독 (스크롤 중엔 번역 보류).
  useEffect(() => {
    const h = (s: boolean) => setScrolling(s);
    ScrollEmitter.on('change', h);
    setScrolling(getScrolling());
    return () => { ScrollEmitter.removeListener('change', h); };
  }, [])

  // 자동 번역: 토글 ON + 화면에 보이고(isIntersecting) + 스크롤 멈춤 + 미표시 → translateMessage (캐시 우선)
  useEffect(() => {
    if (!autoOn || !isIntersecting || scrolling || translatedText) return;
    translateMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOn, isIntersecting, scrolling, translatedText])

  if (isDeleted)
    return <></>
  else if (m.msg.senderEmail)
    return <MessageEmailItem m={m} roomUsers={roomUsers} />
  else if (m.msg.autoMessage) {
    return (
      <div className="flex items-center justify-center" id={`msg-${m.msg.id}`}>
        <div className="inline-flex items-center gap-1 text-meta font-bold text-muted-foreground bg-muted rounded-control px-3 py-1.5">
          <span>
            {m.msg.autoMessage == 'joined' && <><Trans>{getDisplayName(user)} has joined.</Trans>{m.msg.autoMessageUser && <Trans>Invited by {getDisplayName(m.msg.autoMessageUser)}.</Trans>}</>
            }
            {m.msg.autoMessage == 'left' && <><Trans>{getDisplayName(user)} has left.</Trans></>}
            {m.msg.autoMessage == 'removed' && <><Trans>{getDisplayName(user)} has removed {m.msg.autoMessageUser?.name ? getDisplayName(m.msg.autoMessageUser) : ''}.</Trans></>}
            {m.msg.autoMessage == 'added' && <><Trans>{getDisplayName(user)} has added {m.msg.autoMessageUser?.name ? getDisplayName(m.msg.autoMessageUser) : ''}.</Trans></>}
            {m.msg.autoMessage == 'call-start' && <><Trans>{getDisplayName(user)} started a call.</Trans></>}
            {m.msg.autoMessage == 'memo-created' && <><Trans>{getDisplayName(user)} created a new memo: "<MemoTitleLink msg={m.msg} />".</Trans></>}
            {m.msg.autoMessage == 'memo-updated' && <><Trans>{getDisplayName(user)} updated a memo: "<MemoTitleLink msg={m.msg} />".</Trans></>}
            {m.msg.autoMessage == 'pagecall-created' && <><Trans>{getDisplayName(user)} created a Pagecall meeting: "{m.msg.text}".</Trans></>}
            {m.msg.autoMessage == 'task-created' && <><Trans>{getDisplayName(user)} created a new task: "<TaskTitleLink msg={m.msg} />".</Trans></>}
            {m.msg.autoMessage == 'post-created' && <><Trans>{getDisplayName(user)} created a new post: "<PostTitleLink msg={m.msg} />".</Trans></>}
            {m.msg.autoMessage == 'wiki-page-created' && <><Trans>{getDisplayName(user)} created a new wiki page: "<WikiPageTitleLink msg={m.msg} />".</Trans></>}
          </span>
          <span className="text-meta font-normal text-muted-foreground/70 whitespace-nowrap">{UtilApp.dateStrToTimeAndDay(m.msg.created, My.user.lang)}</span>
        </div>
      </div>
    )
  } else {
    return (
      <div ref={rootRef} className={cn(
        "text-body-lg font-normal leading-[24px] text-[color:rgb(var(--text-body))]",
        state
      )} id={`msg-${m.msg.id}`}>
        {isBlocked && (
          <div className="pl-4 text-muted-foreground cursor-pointer" onClick={() => setShowUnblockAlert(true)}>
            {parent && <img src="/assets/spc/keyboard-return.svg" className="inline-block w-4 h-4 mr-2" />}
            <Trans>Blocked message by {getDisplayName(user)}</Trans>
          </div>
        )}
        {!isBlocked && <>
          {isNew && <img className="absolute top-0 right-0 w-5 h-5" src="assets/icon/ic-new-red-18.svg" />}
          {/* 🔧 Figma 레이아웃: 헤더(아바타+이름/날짜+액션) → 본문(full-width) */}
          {/* 헤더 행: 아바타 + 이름/날짜 + 액션 버튼 */}
          <div className="flex gap-3 items-start cursor-pointer" onClick={() => onMessageClick(m)}>
            {/* 아바타 */}
            <div className="shrink-0 relative">
              <UserAvatar src={user.photoUrl} name={getDisplayName(user)} className="w-8 h-8" />
              {!!m.msg.isExternal && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-meta-sm font-bold text-white bg-[color:rgb(var(--badge-api-bg))] px-1 py-px rounded-sm leading-none whitespace-nowrap">
                  <Trans>외부</Trans>
                </span>
              )}
            </div>

            {/* 이름 + 날짜 (세로 정렬) */}
            <div className="flex-1 min-w-0">
              <MessageUserNames />
              <div className={cn("text-xs text-muted-foreground", parent ? "" : "pt-0.5")}>
                {UtilApp.dateStrToTimeAndDay(m.msg.created, My.user.lang)}
              </div>
            </div>

            {/* 액션 버튼들 */}
            <div className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
              {!m.isLocal && <>
                {(() => {
                  const room = My.Rooms.get(m.msg.roomId);
                  const isReadOnly = room?.isReadOnly === true;
                  return !isReadOnly ? (
                    <Button
                      className="h-7 w-7 p-0 text-[color:rgb(var(--msg-icon))] hover:text-[color:rgb(var(--msg-icon-hover))] rounded-md disabled:opacity-50"
                      size="sm"
                      variant="ghost"
                      disabled={isTranslating}
                      onClick={(e) => { e.stopPropagation(); translateMessage(); }}
                      aria-label={t`Translate`}
                    >
                      {isTranslating ? (
                        <div className="animate-spin h-4 w-4 border-2 border-muted-foreground/60 border-t-transparent rounded-full" />
                      ) : (
                        <Languages className="h-4 w-4" />
                      )}
                    </Button>
                  ) : null;
                })()}
                {/* 공유/답글 버튼 (본인 권한이 있는 메시지만) */}
                {m.msg.userIds?.includes(My.user.id) && (
                  <Button className="text-[color:rgb(var(--msg-icon))] hover:text-[color:rgb(var(--msg-icon-hover))] h-7 w-7 p-0" onClick={() => onMessageClick(m)} variant="ghost" size="sm">
                    <IconOutArrowRight className="h-4 w-4" style={{ transform: 'scaleX(-1)' }} />
                  </Button>
                )}
                {isViewButton && (
                  <Button className="text-muted-foreground hover:text-[color:rgb(var(--text-body))] h-7 w-7 p-0" onClick={viewMessageGroup} variant="ghost" size="sm">
                    <MessagesSquare className="h-4 w-4" />
                  </Button>
                )}
                <MessagePopover m={m} state={state} onEdit={() => { setIsEditing(true); setEditText(m.msg.text || ''); }} onSummarize={summarize} />
              </>
              }
              {m.isLocal && <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />}
            </div>
          </div>

          {/* 메시지 본문: full-width, 들여쓰기 없음 (Figma 기준) */}
          <div className="mt-1.5">
            <ShowEmoji />
            <div className={cn(parent && "ml-5")}>
              <PlayMessageButton m={m} />
            </div>
            {messageText && !isEditing && (
              <div className="relative block w-full">
                <div className={cn("border-0 p-0 m-0 block w-full text-body-lg font-normal leading-[24px] text-[color:rgb(var(--text-body))]", expandable && "max-h-[250px] overflow-hidden relative")} ref={divRef}>
                  <div>
                    {m.msg?.user?.id?.startsWith('ai-docubot') && <span className="float-right"><BotSourceTextModal text={m.msg.text} /></span>}
                    <div className="whitespace-pre-wrap break-words" onTouchStart={() => longPress.touchStart()} onTouchEnd={() => longPress.touchEnd()}>
                      {m.msg.recording && <Mic className="w-3.5 h-3.5 inline-block mr-1 text-purple-500 align-text-bottom" />}
                      <span dangerouslySetInnerHTML={{ __html: messageText }} />
                      {m.msg.isEdited && <span className="text-meta-sm text-muted-foreground ml-1.5 align-baseline">(<Trans>edited</Trans>)</span>}
                    </div>
                    {translatedText && <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border-l-4 border-blue-400">
                      <div className="text-sm text-[color:rgb(var(--text-body))] whitespace-pre-wrap"><Languages className="inline h-4 w-4 mr-1" /><span dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(translatedText, m.msg.user?.id) }} /></div>
                    </div>
                    }
                    {summarizedText && <div className="mt-3 p-3 bg-green-500/10 rounded-lg border-l-4 border-green-400">
                      <div className="text-sm text-[color:rgb(var(--text-body))] whitespace-pre-wrap"><IconSparkles className="inline h-4 w-4 mr-1" />{summarizedText}</div>
                    </div>
                    }
                    {isTranslating && (
                      <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border-l-4 border-blue-400 flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full shrink-0" />
                        <span className="text-sm text-muted-foreground"><Trans>Translating...</Trans></span>
                      </div>
                    )}
                    {isSummarizing && (
                      <div className="mt-3 p-3 bg-green-500/10 rounded-lg border-l-4 border-green-400 flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-green-400 border-t-transparent rounded-full shrink-0" />
                        <span className="text-sm text-muted-foreground"><Trans>Summarizing...</Trans></span>
                      </div>
                    )}
                  </div>
                  {expandable && (
                    <div className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none" style={{ background: 'linear-gradient(to top, rgb(var(--message-bubble)) 5%, transparent 100%)' }} />
                  )}
                </div>
                {expandable && (
                  <div className="absolute bottom-[-10px] left-1/2 -translate-x-1/2 flex items-center justify-center p-1.5 rounded-full bg-background border border-border shadow-sm hover:bg-muted transition-all cursor-pointer z-10" onClick={(e) => { e.stopPropagation(); stayExpandedRef.current = true; setExpandable(false); }}>
                    <img src='assets/icon/ic-arrow-small-down-gray-24.svg' className="w-5 h-5 opacity-70" />
                  </div>
                )}
              </div>
            )}
            {isEditing && (
              <div className="flex flex-col gap-2">
                <textarea
                  className="w-full min-h-[60px] p-2 bg-background border border-primary/30 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setIsEditing(false); }
                  }}
                />
                <div className="flex items-center gap-1.5 justify-end">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setIsEditing(false)}>
                    <X className="h-3.5 w-3.5 mr-1" />
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-primary hover:bg-primary/90"
                    disabled={!editText.trim() || editText === m.msg.text}
                    onClick={async () => {
                      try {
                        await Fire.msg.editMessage(m.msg.id, editText.trim());
                        m.msg.text = editText.trim();
                        m.msg.isEdited = true;
                        setIsEditing(false);
                        setMessageText(getMessageText(editText.trim()));
                        toast({ title: t`Message edited`, duration: 1500 });
                      } catch (e: any) {
                        toast({ title: e.message || t`Failed to edit message`, variant: 'destructive' });
                      }
                    }}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    <Trans>Save</Trans>
                  </Button>
                </div>
              </div>
            )}
            {m.msg.files?.length > 0 && <div className="message-files flex flex-wrap gap-2 mt-2">{m.msg.files.sort((a, b) => a.filename < b.filename ? -1 : 1).map((file, i) => <MessageFileItem file={file} key={i} files={m.msg.files} showTitle={false} />)}</div>}
            {m.sendFiles.length > 0 && <div className="added-files-preview flex flex-wrap gap-2 mt-2">{m.sendFiles.map((file, i) => getFileItem(file, i))}</div>}
            {getVideoElement(m)}

            {/* Webhook 전송 결과 인디케이터 */}
            <WebhookDeliveryStatus m={m} />
            <AIComments m={m} />
            {m.msg.inProgress && <div className="animate-spin h-5 w-5 border-2 border-muted-foreground border-t-transparent rounded-full mt-2" />}
          </div>
        </>}

        {/* Unblock Alert Dialog */}
        {isBlocked && (
          <AlertDialog open={showUnblockAlert} onOpenChange={setShowUnblockAlert}>
            <AlertDialogContent className="z-[70]">
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Do you want to unblock {isBlocked.blockedUser.name} to see messages?</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans>Messages by unblocked users are hidden for you. We don't inform {isBlocked.blockedUser.name} that you have unblocked.</Trans>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={() => removeUserBlock(isBlocked)}><Trans>Unblock</Trans></AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Translation Error Dialog */}
        <AlertDialog open={showTranslateError} onOpenChange={setShowTranslateError}>
          <AlertDialogContent className="z-[70]">
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Translation failed</Trans></AlertDialogTitle>
              <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction><Trans>OK</Trans></AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Summarize Error Dialog */}
        <AlertDialog open={showSummarizeError} onOpenChange={setShowSummarizeError}>
          <AlertDialogContent className="z-[70]">
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Summary failed</Trans></AlertDialogTitle>
              <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction><Trans>OK</Trans></AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  function getFileItem(file: File, index: number) {
    let fileKey = `${m.msg.id}/${file.name}`;
    if (!progressForFile || !progressForFile[fileKey])
      return null;
    const sendFile = progressForFile[fileKey];
    let progress = progressForFile[fileKey].progress;

    return <div key={index} className="file-preview relative bg-muted rounded-lg p-3 border border-border">
      {file.type.includes('image')
        ?
        <img src={URL.createObjectURL(file)} title={file.name} className="thumb max-h-24 rounded" />
        :
        <>
          <FileIcon filename={file.name} />
          <p className="file-name text-sm mt-2 text-[color:rgb(var(--text-body))]">{file.name}</p>
          <p className="text-xs text-muted-foreground">{Util.niceBytes(file.size)}</p>
        </>
      }
      {progress < 1 && <Progress value={progress * 100} className="mt-2" />}
      {progress == 1 && <CheckCircle className="absolute top-2 right-2 h-5 w-5 text-green-500" />}
    </div>
  }

  function ShowEmoji() {
    if (m.msg.emoji)
      return <div>
        <img src={`assets/emoji/${m.msg.emoji}.png`} className="w-16 h-16" />
      </div>
    else
      return <></>
  }

  function MessageUserNames() {
    const isMyMessage = user.id === My.user.id;
    const isExternalMsg = !!m.msg.isExternal;
    const visitorName = (m.msg as any).visitorName;
    // 토큰 이름: tokenId로 roomTokens에서 조회 (동적 해석, room.users 패턴과 동일)
    const tokenId = (m.msg as any).tokenId;
    const resolvedTokenName = isExternalMsg && tokenId && roomTokens
      ? roomTokens.find(t => t.id === tokenId)?.name
      : undefined;
    // 토큰 이름이 있으만 user.name 대신 사용
    const displayUserName = resolvedTokenName || getDisplayName(user);
    return <div className="flex items-center gap-1.5 flex-wrap">
      <div className="font-semibold text-[color:rgb(var(--text-title))]">{displayUserName}</div>
      {isMyMessage && (
        <span className="text-meta font-semibold text-[color:rgb(var(--msg-badge-text))] bg-[rgb(var(--msg-badge-bg))] border border-[color:var(--msg-badge-border)] px-1 py-0.5 rounded-md leading-none">
          <Trans>Me</Trans>
        </span>
      )}

      {isExternalMsg && visitorName && (
        <span className="text-meta text-muted-foreground font-medium">
          {visitorName}
        </span>
      )}
      {m.msg.replyTo &&
        <div className="flex items-center text-xs text-muted-foreground">
          <ChevronRight className="w-3 h-3 mx-0.5 text-muted-foreground" />
          <span
            className="font-semibold text-[color:rgb(var(--text-title))] cursor-pointer hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              if (m.msg.replyToMessageId && onReplyToClick) {
                onReplyToClick(m.msg.replyToMessageId);
              }
            }}
          >
            {getDisplayName(m.msg.replyTo)}
          </span>
        </div>
      }
    </div>
  }

  function getVideoElement(m: UIMessage) {
    if (m.msg.videoUrl) {
      if (UtilApp.canOnlyPlayMP4() && !m.msg.videoUrl.includes('.mp4') && !m.msg.videoUrlProcessed?.includes('.mp4')) {
        return <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg text-sm text-blue-700">
          <Info className="h-4 w-4" />
          <Trans>Video is being processed for iOS Safari.</Trans>
        </div>
      }
      return <div className="mt-2">
        <video controls={true} className="block max-w-[300px] max-h-[200px] mx-5 my-2.5 rounded">
          {m.msg.videoUrlProcessed && <source src={`${m.msg.videoUrlProcessed}#t=0.1`} type={m.msg.videoUrlProcessed.includes('.mp4') ? 'video/mp4' : 'video/webm'} />}
          <source src={`${m.msg.videoUrl}#t=0.1`} type={m.msg.videoUrl.includes('.mp4') ? 'video/mp4' : 'video/webm'} />
        </video>
      </div>
    }
    else
      return <></>
  }

  async function viewMessageGroup() {
    setMessageToShow(m.msg);
  }

  async function removeUserBlock(block: UserBlock) {
    My.UserBlocks.delete(block.blockedUser.id);
    await Fire.deleteUserBlock(My.user.id, block.id);
    RoomRefreshEmitter.emit(m.msg.roomId)

    toast({
      title: t`Unblocked ${block.blockedUser.name}`,

    })
    setShowUnblockAlert(false);
  }

  async function copyTextToClipboard() {
    if (m.msg.text) {
      await copyToClipboard(m.msg.text);
      toast({
        title: t`Copied to clipboard`,
        duration: 1500,
      });
    }
  }

  async function translateMessage() {
    if (!m.msg.text) return;
    const targetLang = i18n.locale === 'ko' ? 'ko' : i18n.locale === 'ja' ? 'ja' : 'en';
    const languageName = UtilApp.getLanguageName(i18n.locale); // UI 표시용

    // 공유 캐시 히트 → 즉시 표시 (재번역·재호출 없음). pv 불일치(옛 프롬프트)면 캐시 무시하고 재번역
    const cached = m.msg.translations?.[targetLang];
    if (cached?.status === 'done' && cached.text && cached.pv === TRANSLATE_PROMPT_VERSION) {
      setTranslatedText(cached.text);
      return;
    }

    setIsTranslating(true);
    setTranslatedText(null);
    try {
      const res = await getFunc('translateMessage')({ msgId: m.msg.id, targetLang }) as { data?: { text?: string; pending?: boolean } };
      const data = res?.data;
      if (data?.text) {
        setTranslatedText(data.text);
        setIsTranslating(false);
      } else if (data?.pending) {
        // 다른 사용자가 번역 중 — 메시지 구독으로 done 수신 시 아래 useEffect 가 표시 (isTranslating 유지)
      } else {
        setIsTranslating(false);
        setErrorMessage(t`Could not translate to ${languageName}.`);
        setShowTranslateError(true);
      }
    } catch (error: any) {
      setIsTranslating(false);
      setErrorMessage(error?.message || t`Could not translate to ${languageName}.`);
      setShowTranslateError(true);
    }
  }

  async function summarize(text: string) {
    if (!text) return;
    setIsSummarizing(true);
    setSummarizedText(null);
    const message = t`Summarize this text to a short paragraph.` + `\n\n${text}`

    askAI(message).then(answer => {
      setIsSummarizing(false);
      if (!answer || answer.trim() === '') {
        setErrorMessage(t`Could not summarize the message.`);
        setShowSummarizeError(true);
        return;
      }

      const summarizedText = answer.trim();
      setSummarizedText(summarizedText);
    }).catch(error => {
      setErrorMessage(error.message || t`Could not summarize the message.`);
      setShowSummarizeError(true);
      setIsSummarizing(false);
    });
  }
}

const AIComments: React.FC<{ m: UIMessage }> = ({ m }) => {
  const [showAll, setShowAll] = useState(Util.nrMinAgo(m.msg.created) < 3);
  const shortTextLimit = 100;

  return <>
    {m.msg.comments?.map((c, i) => <div className="ai-comment mt-2" style={{ paddingLeft: m.msg.parentMessageId ? 25 : 5 }} key={i}>
      <div onClick={() => setShowAll(!showAll)} className={cn("flex items-start gap-3 p-3 bg-muted rounded-lg cursor-pointer transition-colors hover:bg-muted/80", showAll ? "show-all" : "show-less")}>
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarImage src={c.imageUrl ?? Soribot.DEFAULT_IMAGE_URL} />
          <AvatarFallback>AI</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <small className="font-medium text-[color:rgb(var(--text-title))]">{c.name}</small>
          {showAll && <div className="textarea text-sm mt-1 whitespace-pre-wrap">{c.text}</div>}
          {!showAll && <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{c.text?.length > shortTextLimit ? (c.text.substring(0, shortTextLimit) + t`... more`) : c.text}</div>}
        </div>
        <span className="shrink-0">
          {c.botMessageId && <BotMessageInfoModal botMessageId={c.botMessageId} bmsg={null} />}
        </span>
      </div>
    </div>)}
  </>
}

export function getMessageText(msgText: string | undefined): string {
  let text = msgText?.split('Sources:')[0];
  return UtilApp.getTextAsHTML(text?.trim(), My.user.id)
}

export const GetReactionSummary: React.FC<{ emotes: UIMessage[], highlightedEmoticon?: string | null, roomUsers?: { id: string; name?: string; photoUrl?: string | null }[] }> = ({ emotes, highlightedEmoticon, roomUsers }) => {

  if (emotes.length > 0) {
    let smiles = emotes.filter(m => m.msg.emoticon == 'smile').length;
    let hearts = emotes.filter(m => m.msg.emoticon == 'heart').length;
    let thumbs = emotes.filter(m => m.msg.emoticon == 'thumbs-up').length;
    let hands = emotes.filter(m => m.msg.emoticon == 'shake-hand').length;
    let checks = emotes.filter(m => m.msg.emoticon == 'check').length;
    let rockets = emotes.filter(m => m.msg.emoticon == 'rocket').length;
    let parties = emotes.filter(m => m.msg.emoticon == 'party').length;
    let hasNew = !!emotes.find(m => m.isNew());

    // 🔧 로컬 리액션 체크 (아직 서버에서 확인 안됨)
    const localEmoticons = emotes.filter(m => m.isLocal).map(m => m.msg.emoticon);

    // 🔧 강조 애니메이션 스타일
    const getHighlightClass = (emoticon: string) => {
      if (highlightedEmoticon === emoticon) {
        return "animate-bounce bg-primary/15 ring-2 ring-orange-400 rounded-lg p-1";
      }
      return "";
    };

    // 🔧 로컬 로딩 애니메이션 스타일 (fade in/out)
    const getLoadingClass = (emoticon: string) => {
      if (localEmoticons.includes(emoticon as any)) {
        return "animate-pulse opacity-60";
      }
      return "";
    };

    return (
      <Popover>
        <PopoverTrigger asChild>
          <div className={cn("flex flex-wrap gap-2 p-2 bg-muted rounded-lg cursor-pointer mt-[3px] w-fit", hasNew && "border-2 border-primary/60")}>
            {smiles > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('smile'), getLoadingClass('smile'))}><img src="/assets/icon/ic-smile-24.svg" className="w-5 h-5" /> <span className="text-sm">{smiles}</span></div>}
            {hearts > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('heart'), getLoadingClass('heart'))}><img src="/assets/icon/ic-heart-24.svg" className="w-5 h-5" /> <span className="text-sm">{hearts}</span></div>}
            {thumbs > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('thumbs-up'), getLoadingClass('thumbs-up'))}><img src="/assets/icon/ic-thumbs-up-24.svg" className="w-5 h-5" /> <span className="text-sm">{thumbs}</span></div>}
            {hands > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('shake-hand'), getLoadingClass('shake-hand'))}><img src="/assets/icon/ic-shake-hand-24.svg" className="w-5 h-5" /> <span className="text-sm">{hands}</span></div>}
            {checks > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('check'), getLoadingClass('check'))}><img src="/assets/icon/ic-check-24.svg" className="w-5 h-5" /> <span className="text-sm">{checks}</span></div>}
            {rockets > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('rocket'), getLoadingClass('rocket'))}>🚀 <span className="text-sm">{rockets}</span></div>}
            {parties > 0 && <div className={cn("flex items-center gap-1", getHighlightClass('party'), getLoadingClass('party'))}>🎉 <span className="text-sm">{parties}</span></div>}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-2"
          side="top"
          align="start"
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {emotes.sort((a, b) => a.msg.created < b.msg.created ? 1 : -1).map(m => {
              let imgfile = `/assets/icon/ic-${m.msg.emoticon}-24.svg`;
              return (
                <div key={m.msg.id} className={cn("flex items-center gap-2 px-2 py-1.5 rounded-md", m.isNew() && "bg-destructive/10")}>
                  <img src={imgfile} className="w-5 h-5 shrink-0" />
                  <UserAvatar src={resolveMessageUser(m.msg.user, roomUsers).photoUrl} name={getDisplayName(resolveMessageUser(m.msg.user, roomUsers))} className="w-6 h-6 shrink-0" />
                  <span className="font-medium text-xs text-[color:rgb(var(--text-title))] truncate">{getDisplayName(resolveMessageUser(m.msg.user, roomUsers))}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  else
    return <></>
}

const WebhookDeliveryStatus: React.FC<{ m: UIMessage }> = ({ m }) => {
  const firestoreDelivery = (m.msg as any).webhookDelivery;

  const [retryResult, setRetryResult] = useState<{ ok: boolean; status?: number; error?: string } | null>(null);
  const [sending, setSending] = useState(false);

  const delivery = retryResult || firestoreDelivery;

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSending(true);
    try {
      const result = await Fire.msg.sendWebhookForMessage(m.msg.id);
      setRetryResult({ ok: result.ok, status: result.status, error: result.error });
    } catch (err: any) {
      setRetryResult({ ok: false, error: err.message || 'Retry failed' });
    } finally {
      setSending(false);
    }
  };

  if (!delivery && !sending) return null;

  if (sending) {
    return (
      <div className="flex items-center gap-1 mt-1 text-meta-sm text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span><Trans>Sending externally...</Trans></span>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex items-center gap-1 mt-1 text-meta-sm",
      delivery.ok ? "text-[color:rgb(var(--badge-api-bg))]" : "text-destructive"
    )}>
      {delivery.ok ? (
        <>
          <CheckCircle className="w-3 h-3" />
          <span><Trans>Delivered externally</Trans></span>
        </>
      ) : (
        <>
          <XCircle className="w-3 h-3" />
          <span><Trans>External delivery failed</Trans>{delivery.status ? ` (${delivery.status})` : ''}{delivery.error ? `: ${delivery.error}` : ''}</span>
          <button
            onClick={handleRetry}
            className="ml-1 inline-flex items-center gap-0.5 text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
          >
            <RefreshCw className="w-3 h-3" />
            <span><Trans>Retry</Trans></span>
          </button>
        </>
      )}
    </div>
  );
};

const MemoTitleLink: React.FC<{ msg: Message }> = ({ msg }) => {
  const hasLink = !!msg.autoMessageItemId;
  return (
    <span
      className={hasLink ? "text-primary font-semibold cursor-pointer hover:underline" : ""}
      onClick={hasLink ? (e) => { e.stopPropagation(); MemoModalEmitter.emit('view', msg.autoMessageItemId); } : undefined}
    >
      {msg.text}
    </span>
  );
}

const TaskTitleLink: React.FC<{ msg: Message }> = ({ msg }) => {
  const hasLink = !!msg.autoMessageItemId;
  return (
    <span
      className={hasLink ? "text-primary font-semibold cursor-pointer hover:underline" : ""}
      onClick={hasLink ? (e) => { e.stopPropagation(); TaskEmitter.emit('open', msg.roomId, msg.autoMessageItemId); } : undefined}
    >
      {msg.text}
    </span>
  );
}

/** PostTitleLink — autoMessageItemId 형식: "boardId:postId" */
const PostTitleLink: React.FC<{ msg: Message }> = ({ msg }) => {
  const raw = msg.autoMessageItemId ?? '';
  const [boardId, postId] = raw.split(':');
  const hasLink = !!boardId && !!postId;
  return (
    <span
      className={hasLink ? "text-primary font-semibold cursor-pointer hover:underline" : ""}
      onClick={hasLink ? (e) => { e.stopPropagation(); PostEmitter.emit('open', msg.roomId, boardId, postId); } : undefined}
    >
      {msg.text}
    </span>
  );
}

/** WikiPageTitleLink — autoMessageItemId 형식: "boardId:postId" (위키 페이지) */
const WikiPageTitleLink: React.FC<{ msg: Message }> = ({ msg }) => {
  const raw = msg.autoMessageItemId ?? '';
  const [boardId, postId] = raw.split(':');
  const hasLink = !!boardId && !!postId;
  return (
    <span
      className={hasLink ? "text-primary font-semibold cursor-pointer hover:underline" : ""}
      onClick={hasLink ? (e) => { e.stopPropagation(); PostEmitter.emit('open', msg.roomId, boardId, postId); } : undefined}
    >
      {msg.text}
    </span>
  );
}

export default MessageItem;